import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    getNotifications, markNotificationRead,
    markAllNotificationsRead, deleteNotification,
} from 'services/timeOff';
import routes from 'constants/routes';

const TYPE_ICONS = {
    time_off_submitted:   { icon: '📋', color: '#1565C0' },
    time_off_approved:    { icon: '✅', color: '#2e7d32' },
    time_off_denied:      { icon: '❌', color: '#c62828' },
    payroll_approved:     { icon: '💰', color: '#1B75F0' },
    paycheck_ready:       { icon: '💵', color: '#2e7d32' },
    document_uploaded:    { icon: '📄', color: '#6a1b9a' },
    timecard_approved:    { icon: '⏱', color: '#00695c' },
    timecard_rejected:    { icon: '⏱', color: '#c62828' },
    onboarding_completed: { icon: '🎉', color: '#2e7d32' },
    onboarding_pending:   { icon: '👤', color: '#1B75F0' },
    benefit_enrollment:   { icon: '🏥', color: '#1B75F0' },
    profile_update:       { icon: '👤', color: '#1565C0' },
    w4_update:            { icon: '📝', color: '#c62828' },
    contractor_update:    { icon: '🤝', color: '#6a1b9a' },
    general:              { icon: '🔔', color: '#555' },
};

const fmtAge = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)   return 'just now';
    if (mins < 60)  return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)   return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7)   return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const NotificationBell = ({ recipientId, recipientType }) => {
    const [open, setOpen] = useState(false);
    const panelRef = useRef(null);
    const navigate = useNavigate();
    const qc = useQueryClient();
    const queryKey = ['notifications', recipientId, recipientType];

    const { data = [] } = useQuery({
        queryKey,
        queryFn: () => getNotifications({ recipientId, recipientType }),
        enabled: !!recipientId && !!recipientType,
        refetchInterval: 30000, // poll every 30s
        staleTime: 15000,
    });

    const notifications = Array.isArray(data) ? data : [];
    const unread = notifications.filter(n => !n.isRead).length;

    const mutRead = useMutation({
        mutationFn: markNotificationRead,
        onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

    const mutReadAll = useMutation({
        mutationFn: () => markAllNotificationsRead({ recipientId, recipientType }),
        onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

    const mutDelete = useMutation({
        mutationFn: deleteNotification,
        onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

    // Normalize legacy/incorrect links to their correct routes
    const resolveLink = (link) => {
        if (!link) return null;
        const fixes = {
            '/employee-portal/documents':   '/employee/documents',
            '/employee-portal':             '/employee',
            '/employee/dashboard':          '/employee',
            '/contractor-portal/dashboard': '/contractor-portal',
        };
        return fixes[link] ?? link;
    };

    const handleClick = useCallback((n) => {
        if (!n.isRead) mutRead.mutate(n.id);
        const link = resolveLink(n.link);
        if (link) navigate(link);
        setOpen(false);
    }, [mutRead, navigate]);

    useEffect(() => {
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    if (!recipientId) return null;

    return (
        <div ref={panelRef} style={{ position: 'relative' }}>
            {/* Bell button */}
            <button
                onClick={() => setOpen(o => !o)}
                style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 8, display: 'flex', alignItems: 'center' }}
                title="Notifications"
            >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={open ? '#1B75F0' : '#555'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unread > 0 && (
                    <span style={{
                        position: 'absolute', top: 0, right: 0,
                        background: '#dc3545', color: '#fff',
                        fontSize: 10, fontWeight: 700,
                        minWidth: 16, height: 16, borderRadius: 8,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '0 3px', lineHeight: 1,
                    }}>
                        {unread > 99 ? '99+' : unread}
                    </span>
                )}
            </button>

            {/* Dropdown panel */}
            {open && (() => {
                const mobile = window.innerWidth <= 768;
                return (
                <div style={mobile ? {
                    position: 'fixed',
                    top: 60,
                    left: 8,
                    right: 8,
                    maxHeight: 'calc(100vh - 80px)',
                    background: '#fff', borderRadius: 12,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    zIndex: 2000, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                } : {
                    position: 'absolute', right: 0, top: 'calc(100% + 10px)',
                    width: 360, maxHeight: 480,
                    background: '#fff', borderRadius: 12,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    zIndex: 2000, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                }}>
                    {/* Header */}
                    <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>
                            Notifications {unread > 0 && <span style={{ fontSize: 12, color: '#dc3545', fontWeight: 600 }}>({unread} new)</span>}
                        </div>
                        {unread > 0 && (
                            <button onClick={() => mutReadAll.mutate()} style={{ fontSize: 12, color: '#1B75F0', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                                Mark all read
                            </button>
                        )}
                    </div>

                    {/* List */}
                    <div style={{ overflowY: 'auto', flex: 1, maxHeight: 380 }}>
                        {notifications.length === 0 ? (
                            <div style={{ padding: '36px 16px', textAlign: 'center', color: '#aaa' }}>
                                <div style={{ fontSize: 32, marginBottom: 8 }}>🔔</div>
                                <div style={{ fontSize: 13 }}>No notifications yet</div>
                            </div>
                        ) : (
                            notifications.map(n => {
                                const meta = TYPE_ICONS[n.type] || TYPE_ICONS.general;
                                return (
                                    <div
                                        key={n.id}
                                        onClick={() => handleClick(n)}
                                        style={{
                                            display: 'flex', alignItems: 'flex-start', gap: 12,
                                            padding: '12px 16px',
                                            background: n.isRead ? '#fff' : '#EFF6FF',
                                            borderBottom: '1px solid #f5f5f5',
                                            cursor: n.link ? 'pointer' : 'default',
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#f9f9f9'}
                                        onMouseLeave={e => e.currentTarget.style.background = n.isRead ? '#fff' : '#EFF6FF'}
                                    >
                                        <div style={{ fontSize: 22, flexShrink: 0, marginTop: 1 }}>{meta.icon}</div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 13, fontWeight: n.isRead ? 400 : 600, color: '#222', lineHeight: 1.3 }}>{n.title}</div>
                                            {n.message && <div style={{ fontSize: 12, color: '#666', marginTop: 3, lineHeight: 1.4 }}>{n.message}</div>}
                                            <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>{fmtAge(n.createdAt)}</div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                                            {!n.isRead && (
                                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1B75F0' }} />
                                            )}
                                            <button
                                                onClick={e => { e.stopPropagation(); mutDelete.mutate(n.id); }}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: 16, lineHeight: 1, padding: 0 }}
                                                title="Dismiss"
                                            >×</button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer — View all */}
                    <div
                        onClick={() => { setOpen(false); navigate(routes.NOTIFICATIONS.HOME); }}
                        style={{
                            padding: '10px 16px', borderTop: '1px solid #f0f0f0',
                            textAlign: 'center', cursor: 'pointer',
                            fontSize: 13, color: '#1B75F0', fontWeight: 600,
                            background: '#fafafa',
                        }}
                    >
                        View all notifications →
                    </div>
                </div>
                );
            })()}
        </div>
    );
};

export default NotificationBell;
